import { useContext } from 'react';

import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import type {
  BlockSignupPayload,
  DemoRequest,
  DemoRequestListResponse,
  DemoStatus,
  SignupDetail,
  SignupListResponse,
  SignupStats,
  SignupView,
} from '../models/admin-signups.model';

/** docs/plg-review.md → Admin API (superadmin strategy). */
export const ADMIN_SIGNUP_ROUTES = {
  list: '/services/pms/admin/signups',
  stats: '/services/pms/admin/signups/stats',
  detail: (vendorId: string) => `/services/pms/admin/signups/${encodeURIComponent(vendorId)}`,
  action: (vendorId: string, action: 'approve' | 'block' | 'reopen' | 'recheck' | 'sdk-check' | 'mark-email-verified' | 'resend-verification') =>
    `/services/pms/admin/signups/${encodeURIComponent(vendorId)}/${action}`,
  demoRequests: '/services/pms/admin/demo-requests',
  demoRequest: (id: string) => `/services/pms/admin/demo-requests/${encodeURIComponent(id)}`,
} as const;

const query = (params: Record<string, string | number | undefined | null>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
};

/** A list the API may send as `{ rows, total }` or as a bare array. */
const asList = <T,>(res: any, page: number, limit: number): { rows: T[]; total: number; page: number; limit: number } =>
  Array.isArray(res)
    ? { rows: res as T[], total: res.length, page, limit }
    : { rows: (res?.rows ?? []) as T[], total: Number(res?.total ?? res?.rows?.length ?? 0), page: Number(res?.page ?? page), limit: Number(res?.limit ?? limit) };

export const useAdminSignupsApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);
  const send = (method: ApiMethod, path: string, body?: any) => sendAuthGuardedRequest(method, path, body) as Promise<any>;

  return {
    /** `page` is 1-based, like the vendors list. */
    list: async (params: { view?: SignupView; q?: string; days?: number; page?: number; limit?: number }): Promise<SignupListResponse> => {
      const page = params.page ?? 1;
      const limit = params.limit ?? 25;
      return asList(await send(ApiMethod.GET, `${ADMIN_SIGNUP_ROUTES.list}${query({ ...params, page, limit })}`), page, limit);
    },
    stats: (days = 30): Promise<SignupStats> => send(ApiMethod.GET, `${ADMIN_SIGNUP_ROUTES.stats}${query({ days })}`),
    detail: (vendorId: string): Promise<SignupDetail> => send(ApiMethod.GET, ADMIN_SIGNUP_ROUTES.detail(vendorId)),
    /** Approve; optionally with the trial length and the prices this vendor will see (docs/pricing-v2.md). */
    approve: (vendorId: string, body: { note?: string; trialDays?: number; offer?: unknown } = {}): Promise<SignupDetail> =>
      send(ApiMethod.POST, ADMIN_SIGNUP_ROUTES.action(vendorId, 'approve'), body),
    block: (vendorId: string, body: BlockSignupPayload): Promise<SignupDetail> => send(ApiMethod.POST, ADMIN_SIGNUP_ROUTES.action(vendorId, 'block'), body),
    reopen: (vendorId: string): Promise<SignupDetail | null> => send(ApiMethod.POST, ADMIN_SIGNUP_ROUTES.action(vendorId, 'reopen')),
    recheck: (vendorId: string): Promise<unknown> => send(ApiMethod.POST, ADMIN_SIGNUP_ROUTES.action(vendorId, 'recheck')),
    /** Look for the SDK on the site now; answers the detail with the new `sdkPresence`. */
    sdkCheck: (vendorId: string): Promise<SignupDetail | null> => send(ApiMethod.POST, ADMIN_SIGNUP_ROUTES.action(vendorId, 'sdk-check')),
    markEmailVerified: (vendorId: string): Promise<SignupDetail | null> => send(ApiMethod.POST, ADMIN_SIGNUP_ROUTES.action(vendorId, 'mark-email-verified')),
    resendVerification: (vendorId: string): Promise<unknown> => send(ApiMethod.POST, ADMIN_SIGNUP_ROUTES.action(vendorId, 'resend-verification')),

    demoRequests: async (params: { status?: DemoStatus; page?: number; limit?: number } = {}): Promise<DemoRequestListResponse> => {
      const page = params.page ?? 1;
      const limit = params.limit ?? 25;
      return asList<DemoRequest>(await send(ApiMethod.GET, `${ADMIN_SIGNUP_ROUTES.demoRequests}${query({ ...params, page, limit })}`), page, limit);
    },
    updateDemoRequest: (id: string, patch: { status?: DemoStatus; note?: string }): Promise<DemoRequest | null> =>
      send(ApiMethod.PATCH, ADMIN_SIGNUP_ROUTES.demoRequest(id), patch),
  };
};
